import {useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {ArrowLeft} from "lucide-react";
import VideoCard from "./VideoCard";
import getFurthestPosition from "../utils/getFurthestPosition";
import SectionNav from "./SectionNav";
import PlaylistRow from "./PlaylistRow";

// Browse view: Section -> Playlist -> ordered videos. Replaces the flat
// category grid when the learner isn't actively searching. The "See all"
// playlist detail lives in the URL (`?playlist=<id>`), so it is a shareable
// permalink, survives refresh, and the browser Back button exits it. The
// player itself is URL-driven too (`?v=`) and rendered by VideoDashboard;
// cards just report the tapped video up via onOpenVideo.
const CatalogBrowser = ({
  catalog,
  watchHistory,
  updateDailyStat,
  onSeeAllActiveChange,
  onOpenVideo,
}) => {
  const {intro = [], sections = []} = catalog || {};
  const [searchParams, setSearchParams] = useSearchParams();
  // "See all" playlist-detail view: which playlist is open, from the URL.
  const seeAllId = searchParams.get("playlist");
  const [selectedId, setSelectedId] = useState(sections[0]?.id);

  // Entering/leaving the detail view are navigations (push), so Back walks
  // out of the detail view naturally.
  const openSeeAll = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("playlist", id);
    setSearchParams(next);
  };
  const closeSeeAll = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("playlist");
    setSearchParams(next);
  };

  // Keep the selected section valid as the catalog loads/changes.
  useEffect(() => {
    if (sections.length && !sections.some((s) => s.id === selectedId)) {
      setSelectedId(sections[0].id);
    }
  }, [sections, selectedId]);

  // Resolve the open playlist across ALL sections (a ?playlist= deep-link
  // can point anywhere), and keep the section tabs in step with it so
  // closing the detail lands on the owning section's browse rows.
  const ownerSection =
    (seeAllId &&
      sections.find((s) => s.playlists.some((p) => p.id === seeAllId))) ||
    null;
  const seeAllPlaylist =
    ownerSection?.playlists.find((p) => p.id === seeAllId) || null;
  useEffect(() => {
    if (ownerSection && ownerSection.id !== selectedId) {
      setSelectedId(ownerSection.id);
    }
  }, [ownerSection, selectedId]);

  const section = sections.find((s) => s.id === selectedId) || sections[0];

  // A ?playlist= that doesn't resolve once the catalog is in (bad/stale
  // link, or a playlist that fell under the size threshold) falls back to
  // the browse rows. replace: junk URLs aren't history states worth keeping.
  useEffect(() => {
    if (!seeAllId || !sections.length || seeAllPlaylist) return;
    const next = new URLSearchParams(searchParams);
    next.delete("playlist");
    setSearchParams(next, {replace: true});
  }, [seeAllId, sections, seeAllPlaylist, searchParams, setSearchParams]);

  // Let the dashboard hide its header (tagline + progress card) while the
  // detail view is open, so "Back" sits at the top. The cleanup resets it
  // if we unmount mid-detail (e.g. the learner starts a search).
  useEffect(() => {
    if (!onSeeAllActiveChange) return;
    onSeeAllActiveChange(Boolean(seeAllPlaylist));
    return () => onSeeAllActiveChange(false);
  }, [seeAllPlaylist, onSeeAllActiveChange]);

  // The dashboard's scroll container keeps its position when the view under
  // it swaps, so a learner entering/leaving the detail view mid-page would
  // land mid-list. Snap to the top of the browser on every view change —
  // but not on the initial mount, which would scroll past the stats header.
  const topRef = useRef(null);
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    topRef.current?.scrollIntoView({block: "start"});
  }, [seeAllId]);

  const renderCard = (v) => (
    <VideoCard
      key={v.id}
      video={v}
      savedTime={watchHistory[v.id]?.lastPosition || 0}
      furthestTime={getFurthestPosition(watchHistory[v.id])}
      onInteraction={() => updateDailyStat("clicks")}
      onExpand={() => onOpenVideo(v)}
    />
  );

  return (
    <>
      {/* scroll-mt matches the dashboard container's padding so snapping here
          lands flush under the header instead of clipping. */}
      <div ref={topRef} aria-hidden="true" className="scroll-mt-6" />

      {seeAllPlaylist ? (
        /* "See all" detail: every video in the playlist as a grid, with a way
           back to the browse rows. */
        <section className="mb-4">
          <button
            type="button"
            onClick={closeSeeAll}
            className="flex items-center gap-1 text-sm font-semibold text-[#001489] mb-3 px-1 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to {ownerSection.title}
          </button>
          <h3 className="text-base font-semibold text-[#262626] tracking-tight mb-2 px-1">
            {seeAllPlaylist.title}
            <span className="ml-2 text-xs font-medium text-[#262626]/40">
              {seeAllPlaylist.videos.length}{" "}
              {seeAllPlaylist.videos.length === 1 ? "video" : "videos"}
            </span>
          </h3>
          {/* Single column on phones: full-width cards are easier to tap and
              read for this audience than a cramped 2-up grid. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-2">
            {seeAllPlaylist.videos.map(renderCard)}
          </div>
        </section>
      ) : (
        <>
          {/* Orientation/intro pinned above everything — the program's required
          first step, regardless of which section is selected. Rendered as a
          grid (not a fixed-width row) so a single intro video fills the width
          on mobile instead of leaving the row half-empty. */}
          {intro.length > 0 && (
            <section className="mb-4">
              <h3 className="text-base font-semibold text-[#262626] tracking-tight mb-2 px-1">
                Start here
                <span className="ml-2 text-xs font-medium text-[#262626]/40">
                  {intro.length} {intro.length === 1 ? "video" : "videos"}
                </span>
              </h3>
              {/* Capped on mobile (max-w-xs) so the single intro card isn't a
              full-bleed, oversized thumbnail — the whole orientation video
              stays visible without scrolling. Normal grid from sm up. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2 max-w-xs sm:max-w-none">
                {intro.map(renderCard)}
              </div>
            </section>
          )}

          <SectionNav
            sections={sections}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />

          {section?.playlists.map((p) => (
            <PlaylistRow
              key={p.id}
              title={p.title}
              videos={p.videos}
              renderCard={renderCard}
              onSeeAll={() => openSeeAll(p.id)}
            />
          ))}

          {section?.looseVideos.length > 0 && (
            <section className="mb-4">
              <h3 className="text-base font-semibold text-[#262626] tracking-tight mb-2 px-1">
                More in {section.title}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-2">
                {section.looseVideos.map(renderCard)}
              </div>
            </section>
          )}

          {sections.length === 0 && (
            <div
              role="status"
              className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-[24px] border border-dashed border-slate-200 bg-slate-50/60"
            >
              <p className="text-base font-semibold text-slate-700">
                No videos yet
              </p>
              <p className="text-sm text-slate-400 mt-1">
                Check back soon — your playlists will appear here.
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CatalogBrowser;
